import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { teamMembers, testimonials } from '@/mocks/team';
import { faqs } from '@/mocks/faq';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与你联系。');
      form.reset();
      return;
    }

    setFormStatus('loading');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9sncjdlbrt1qcgaj6eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: (formData.get('name') as string) || '',
          email: (formData.get('email') as string) || '',
          subject: (formData.get('subject') as string) || '',
          message: (formData.get('message') as string) || '',
        }).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢你的留言，我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后再试。' : '提交失败，请检查信息后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-500">
                <i className="ri-shield-star-line text-2xl text-background-50"></i>
              </div>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-4">
                关于男子汉宝藏
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                我们不只是一个好物推荐平台——我们是一群对品质有执念的男人，
                用真实的体验和严苛的标准，帮你从海量商品中筛选出真正值得拥有的宝藏。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-14">
              <div className="text-center p-6 rounded-xl bg-background-100">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-search-eye-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">严苛筛选</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  每件推荐都经过至少30天的实测或深度调研，不接受品牌付费推广。
                </p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-user-heart-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">真实体验</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  团队成员来自户外、穿搭、数码等不同领域，用亲身经历说话。
                </p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-shield-check-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">品质承诺</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  自己不愿意用的东西，绝不推荐给读者。这是我们的底线。
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 bg-background-100 rounded-2xl p-8 md:p-10">
              <div className="md:w-1/3 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Aesthetic%20flat%20lay%20photography%20of%20masculine%20curated%20lifestyle%20items%20including%20vintage%20compass%20leather%20notebook%20fountain%20pen%20pocket%20knife%20and%20coffee%20beans%20on%20dark%20wooden%20surface%2C%20warm%20directional%20lighting%2C%20rich%20brown%20and%20bronze%20tones%2C%20editorial%20product%20photography&width=600&height=600&seq=about-brand-story&orientation=squarish"
                  alt="男子汉宝藏品牌理念"
                  className="w-full rounded-xl object-cover"
                  title="男子汉宝藏 - 品质生活理念"
                />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  男子汉宝藏诞生于2023年，由一群对品质有执念的男性玩家创立。我们发现，市面上的好物推荐要么过于商业导向，要么缺乏深度体验——于是我们决定自己做。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的内容涵盖硬核装备、型男穿搭、数码极客、户外探险、品质生活和腕表鉴赏六大品类，每一篇文章都基于真实的长期使用体验，拒绝浮夸，只讲干货。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的使命很简单：帮每一个真男人找到属于他的宝藏级单品，活出硬朗质感。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                团队介绍
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                一群在各个领域深耕多年的玩家，用热爱和专业为你筛选真正的好东西。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-20 h-20 mx-auto mb-4 rounded-full object-cover"
                    title={`${member.name} - ${member.role}`}
                    loading="lazy"
                  />
                  <h3 className="font-heading font-semibold text-base text-foreground-950 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mb-3">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                来自真实读者的反馈，是我们前行的最大动力。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-fill text-secondary-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 font-bold text-xs">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于男子汉宝藏，你可能想了解的事情。</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                  >
                    <span className="font-heading font-medium text-sm md:text-base text-foreground-950">
                      {faq.question}
                    </span>
                    <i className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-200 shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题、合作意向或建议？欢迎给我们留言。
              </p>
            </div>

            <form
              onSubmit={handleContactSubmit}
              data-readdy-form
              className="space-y-5 bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    placeholder="你的名字"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  placeholder="留言主题"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请输入你的留言，不超过500字..."
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antispam-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="whitespace-nowrap w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-medium text-sm rounded-lg transition-colors"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>
              {formStatus !== 'idle' && (
                <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}